import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyId, EmptyState } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import {
  useAuvikTenants,
  useMapAuvikTenant,
  useUnmapAuvikTenant,
  useSyncAuvikInventory,
  useLocationsForCustomer,
  useDiscoveredAuvikTenants,
  useRegisterDiscoveredAuvikTenant,
  type AuvikTenantRow,
} from '../hooks/useAuvikTenants';
import { useCustomersLite, type CustomerLite } from '../hooks/useCustomerProvisioning';
import { describeJobError } from '../lib/triggerTask';

type Feedback = { tone: 'success' | 'error'; text: string };

const BTN =
  'inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-3 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';

/**
 * Global Auvik tenant mapping screen.
 *
 *   • Unmapped tenants — review an exact customer and location, save.
 *   • Mapped tenants — current assignments with re-map / unmap.
 *
 * "Sync inventory" queues the auvik-sync-tenant-inventory Trigger.dev task
 * through the trigger-task Edge Function. Until a valid Auvik API key is
 * configured the inventory stays empty and the job reports why.
 */
export function AuvikMappingPage() {
  const { role } = useAuth();
  const canManage = role === 'admin' || role === 'superadmin';
  const { data: tenants, isLoading, isError } = useAuvikTenants();
  const discovery = useDiscoveredAuvikTenants(canManage);
  const { data: customers } = useCustomersLite();
  const sync = useSyncAuvikInventory();
  const [syncFeedback, setSyncFeedback] = useState<Feedback | null>(null);
  const [mappingFeedback, setMappingFeedback] = useState<Feedback | null>(null);

  const customerById = useMemo(() => {
    const m = new Map<string, CustomerLite>();
    (customers ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [customers]);

  const [filter, setFilter] = useState<'unmapped' | 'mapped' | 'all'>('unmapped');

  const filtered = useMemo(() => {
    const list = tenants ?? [];
    if (filter === 'unmapped') return list.filter((t) => !t.customer_id);
    if (filter === 'mapped') return list.filter((t) => !!t.customer_id);
    return list;
  }, [tenants, filter]);

  const stats = useMemo(() => {
    const list = tenants ?? [];
    const mapped = list.filter((t) => !!t.customer_id).length;
    return { total: list.length, mapped, unmapped: list.length - mapped };
  }, [tenants]);

  const runSync = async () => {
    setSyncFeedback(null);
    try {
      const result = await sync.mutateAsync();
      setSyncFeedback({
        tone: 'success',
        text: `Inventory sync queued${result.runId ? ` (run ${result.runId})` : ''}. This list refreshes every 30 seconds; the job reports a missing API key on the Integrations page if Auvik is not configured.`,
      });
    } catch (e) {
      setSyncFeedback({ tone: 'error', text: describeJobError(e) });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text">Auvik Tenants</h1>
        <p className="text-sm text-text-muted mt-1">
          Assign each Auvik tenant to an exact customer and location. One customer can own several tenants.
        </p>
      </div>

      {canManage && <section className="rounded-lg border border-border bg-surface p-4 space-y-3" aria-labelledby="discovered-auvik-title">
        <div className="flex flex-wrap justify-between gap-2"><div><h2 id="discovered-auvik-title" className="text-sm font-semibold text-text">Discovered monitoring providers</h2><p className="text-xs text-text-muted">Review an exact provider ID from the copied Portal catalog and choose an existing customer and location. The catalog is available while monitoring is paused.</p></div><button type="button" className={`${BTN} border border-border text-text`} onClick={() => void discovery.refetch()} disabled={discovery.isFetching}>Refresh provider discovery</button></div>
        {discovery.isLoading && <p role="status" className="text-sm text-text-muted">Loading copied provider catalog…</p>}
        {discovery.isError && <p role="alert" className="text-sm text-error">The copied provider catalog is unavailable. Existing Portal assignments stay available below.</p>}
        {(discovery.data ?? []).filter((provider) => !(tenants ?? []).some((tenant) => tenant.auvik_tenant_id === provider.id)).map((provider) => <DiscoveredTenant key={provider.id} provider={provider} customers={customers ?? []} onResult={(result) => { setMappingFeedback(result); if (result.tone === 'success') setFilter('mapped'); }} />)}
        {!discovery.isLoading && !discovery.isError && !(discovery.data ?? []).some((provider) => !(tenants ?? []).some((tenant) => tenant.auvik_tenant_id === provider.id)) && <p className="text-xs text-text-muted">No additional discovered Auvik providers need Portal registration.</p>}
      </section>}
      {mappingFeedback && <p role={mappingFeedback.tone === 'error' ? 'alert' : 'status'} className={`text-sm ${mappingFeedback.tone === 'error' ? 'text-error' : 'text-success'}`}>{mappingFeedback.text}</p>}

      <div className="rounded-lg border border-border bg-surface p-4 flex flex-wrap items-center justify-between gap-3">
        <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <div>
            <dt className="inline text-text-muted">Total tenants: </dt>
            <dd className="inline text-text font-semibold">{stats.total}</dd>
          </div>
          <div>
            <dt className="inline text-text-muted">Mapped: </dt>
            <dd className="inline text-success font-semibold">{stats.mapped}</dd>
          </div>
          <div>
            <dt className="inline text-text-muted">Unmapped: </dt>
            <dd className="inline text-warning font-semibold">{stats.unmapped}</dd>
          </div>
        </dl>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button type="button" onClick={runSync} disabled={sync.isPending} className={`${BTN} border border-border bg-surface text-text hover:bg-surface-strong`}>
            {sync.isPending ? 'Queuing…' : 'Sync inventory from Auvik'}
          </button>
        </div>
        {syncFeedback && (
          <p role={syncFeedback.tone === 'error' ? 'alert' : 'status'} className={`w-full text-xs break-words ${syncFeedback.tone === 'error' ? 'text-error' : 'text-success'}`}>
            {syncFeedback.text}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface">
        <header className="px-4 sm:px-5 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-text">Tenant mapping</h2>
            <p className="text-xs text-text-muted">
              Devices, interfaces and alerts are scoped to a customer through this mapping.
            </p>
          </div>
          <div className="flex gap-1 text-xs" role="group" aria-label="Filter tenants">
            {(['unmapped', 'mapped', 'all'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                aria-pressed={filter === k}
                className={`rounded px-2.5 py-1 min-h-[44px] sm:min-h-[32px] capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  filter === k
                    ? 'bg-primary text-text-inverse'
                    : 'bg-surface-3 text-text-muted hover:text-text'
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </header>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-text-muted" role="status">Loading…</div>
        ) : isError ? (
          <p role="alert" className="p-8 text-center text-sm text-notification">Could not load Auvik tenants. Refresh to try again.</p>
        ) : filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M12 5l7 7-7 7" /></svg>}
              title={stats.total === 0 ? 'No Auvik tenants in inventory' : `No ${filter} tenants`}
              subtitle={
                stats.total === 0
                  ? 'Auvik is not connected. Configure a valid API key, then sync the inventory to populate this list.'
                  : 'Try another filter.'
              }
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((tenant) => (
              <TenantRow
                key={tenant.id}
                tenant={tenant}
                customers={customers ?? []}
                customerById={customerById}
                onResult={setMappingFeedback}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DiscoveredTenant({ provider, customers, onResult }: { provider: { id: string; name: string; ignored?: boolean }; customers: CustomerLite[]; onResult: (result: Feedback) => void }) {
  const [editing, setEditing] = useState(false);
  const row: AuvikTenantRow = { id: '', auvik_tenant_id: provider.id, auvik_tenant_name: provider.name,
    auvik_tenant_attributes: null, customer_id: null, location_id: null, mapped_by_user_id: null, mapped_at: null, created_at: '', updated_at: '' };
  return <div className="rounded border border-border p-3"><div className="flex justify-between gap-3"><div className="text-sm text-text">{provider.name}<p className="text-xs text-text-muted break-all">{provider.id}{provider.ignored ? ' · ignored in monitoring discovery' : ''}</p></div>{!editing && <button type="button" className={`${BTN} bg-primary text-text-inverse`} onClick={() => setEditing(true)}>Review provider assignment</button>}</div>{editing && <MappingForm tenant={row} customers={customers} discovered onClose={() => setEditing(false)} onResult={onResult} />}</div>;
}

function TenantRow({
  tenant,
  customers,
  customerById,
  onResult,
}: {
  tenant: AuvikTenantRow;
  customers: CustomerLite[];
  customerById: Map<string, CustomerLite>;
  onResult: (feedback: Feedback) => void;
}) {
  const { role } = useAuth();
  const canManage = role === 'admin' || role === 'superadmin';
  const isMapped = !!tenant.customer_id;
  const [editing, setEditing] = useState(false);
  const mappedCustomer = tenant.customer_id ? customerById.get(tenant.customer_id) ?? null : null;
  const attrs = tenant.auvik_tenant_attributes as { tenantType?: string; domainPrefix?: string } | null;

  return (
    <li className="px-4 sm:px-5 py-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-text font-medium truncate">
            {tenant.auvik_tenant_name ?? 'Auvik tenant'}
          </div>
          <div className="text-xs text-text-muted flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
            <CopyId value={tenant.auvik_tenant_id} />
            {attrs?.tenantType && <span>{attrs.tenantType}</span>}
            {attrs?.domainPrefix && attrs.domainPrefix !== tenant.auvik_tenant_name && (
              <span>domain: {attrs.domainPrefix}</span>
            )}
          </div>
          {isMapped && (
            <div className="mt-1 text-xs">
              <span className="text-text-muted">→ </span>
              <Link to={`/customers/${tenant.customer_id}`} className="text-primary hover:underline">
                {mappedCustomer?.name ?? 'Customer'}
              </Link>
              {tenant.location_id && <>{' / '}<Link to={`/locations/${tenant.location_id}`} className="text-primary hover:underline">{tenant.location?.name ?? tenant.location_id}</Link></>}
              {tenant.mapped_at && (
                <span className="text-text-muted">
                  {' '}
                  (mapped {new Date(tenant.mapped_at).toLocaleDateString()})
                </span>
              )}
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-wrap gap-2">
          {canManage && !editing && !isMapped && (
            <button type="button" onClick={() => setEditing(true)} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
              Map…
            </button>
          )}
          {canManage && !editing && isMapped && (
            <>
              <button type="button" onClick={() => setEditing(true)} className={`${BTN} border border-border bg-surface text-text hover:bg-surface-strong`}>
                Re-map
              </button>
              <UnmapButton tenant={tenant} onResult={onResult} />
            </>
          )}
        </div>
      </div>
      {canManage && editing && (
        <MappingForm
          tenant={tenant}
          customers={customers}
          onClose={() => setEditing(false)}
          onResult={onResult}
        />
      )}
    </li>
  );
}

function MappingForm({
  tenant,
  customers,
  onClose,
  onResult,
  discovered = false,
}: {
  tenant: AuvikTenantRow;
  customers: CustomerLite[];
  onClose: () => void;
  onResult: (f: Feedback) => void;
  discovered?: boolean;
}) {
  const map = useMapAuvikTenant();
  const register = useRegisterDiscoveredAuvikTenant();
  const [expectedUpdatedAt] = useState(tenant.updated_at);
  const [customerId, setCustomerId] = useState(tenant.customer_id ?? '');
  const [locationId, setLocationId] = useState(tenant.location_id ?? '');
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { data: locations } = useLocationsForCustomer(customerId || null);

  const submit = async () => {
    if (!customerId || !locationId || reason.trim().length < 5) return;
    setLocalError(null);
    try {
      if (discovered) await register.mutateAsync({ provider_id: tenant.auvik_tenant_id,
        customer_id: customerId, location_id: locationId, reason: reason.trim() });
      else await map.mutateAsync({
        id: tenant.id,
        customer_id: customerId,
        location_id: locationId,
        expected_updated_at: expectedUpdatedAt,
        reason: reason.trim(),
      });
      onResult({ tone: 'success', text: 'Provider ownership saved. If this changed a consolidated network, review and resume its monitoring registry before expecting collection.' });
      onClose();
    } catch (e) {
      const text = `Could not save this mapping: ${e instanceof Error ? e.message : String(e)}`;
      setLocalError(text);
      if (discovered) onResult({ tone: 'error', text });
    }
  };

  const select = 'w-full min-h-[44px] md:min-h-[40px] rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

  return (
    <div className="mt-3 sm:ml-4 sm:pl-4 sm:border-l-2 sm:border-primary-soft space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Customer</span>
          <select
            aria-label="Customer"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setLocationId('');
            }}
            className={select}
          >
            <option value="">— Select customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.is_zoho_synced ? '' : '(local only)'}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Location</span>
          <select
            aria-label="Location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={!customerId || (locations?.length ?? 0) === 0}
            className={select}
          >
            <option value="">
              {customerId ? ((locations?.length ?? 0) === 0 ? 'This customer has no locations' : '— Select exact location —') : 'Pick a customer first'}
            </option>
            {(locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.id})
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-xs text-text-muted break-all">Review provider {tenant.auvik_tenant_id} → customer {customerId || 'not selected'} / location {locationId || 'not selected'}.</p>
      <label className="block text-xs text-text-muted">Reason for provider ownership change<input className={`${select} mt-1`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></label>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button type="button" onClick={submit} disabled={!customerId || !locationId || reason.trim().length < 5 || map.isPending || register.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
          {map.isPending || register.isPending ? 'Saving…' : discovered ? 'Create reviewed provider ownership' : 'Save mapping'}
        </button>
        <button type="button" onClick={onClose} className={`${BTN} text-text-muted hover:text-text`}>
          Cancel
        </button>
      </div>
      {localError && <p role="alert" className="text-xs text-error break-words">{localError}</p>}
      <p className="text-xs text-text-muted">
        This review uses the provider revision shown when the form opened. Close and reopen the form if another administrator changes it. <Link to="/network-ops/mappings" className="text-primary underline">Review monitoring registry</Link>.
      </p>
    </div>
  );
}

function UnmapButton({ tenant, onResult }: { tenant: AuvikTenantRow; onResult: (f: Feedback) => void }) {
  const unmap = useUnmapAuvikTenant();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(tenant.updated_at);
  const run = async () => {
    try {
      await unmap.mutateAsync({ id: tenant.id, expected_updated_at: expectedUpdatedAt, reason: reason.trim() });
      onResult({ tone: 'success', text: 'Mapping removed.' });
      setOpen(false); setReason('');
    } catch (e) {
      onResult({ tone: 'error', text: `Could not remove the mapping: ${e instanceof Error ? e.message : String(e)}` });
    }
  };
  return (
    <div>{!open ? <button type="button" onClick={() => { setExpectedUpdatedAt(tenant.updated_at); setOpen(true); }} className={`${BTN} border border-error/40 text-error hover:bg-error-soft`}>Unmap</button> : <div className="space-y-2 max-w-sm"><p className="text-xs text-text-muted">Detach provider {tenant.auvik_tenant_id} from its current customer and location.</p><label className="block text-xs text-text-muted">Reason for detaching provider<input className="mt-1 w-full rounded border border-border bg-surface p-2" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></label><button type="button" onClick={run} disabled={unmap.isPending || reason.trim().length < 5} className={`${BTN} border border-error/40 text-error hover:bg-error-soft`}>{unmap.isPending ? 'Removing…' : 'Confirm unmap'}</button><button type="button" className={`${BTN} text-text-muted`} disabled={unmap.isPending} onClick={() => setOpen(false)}>Cancel</button></div>}</div>
  );
}
